import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Get theme from localStorage or default to 'light'
    return localStorage.getItem("app-theme") || "light";
  });

  const [systemPreference, setSystemPreference] = useState("light");

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      setSystemPreference(e.matches ? "dark" : "light");
    };

    // Set initial system preference
    setSystemPreference(mediaQuery.matches ? "dark" : "light");

    // Listen for changes
    mediaQuery.addListener(handleChange);

    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    let effectiveTheme = theme;
    if (theme === "auto") {
      effectiveTheme = systemPreference;
    }

    // Remove existing theme classes
    root.classList.remove("light-theme", "dark-theme");

    // Add current theme class
    root.classList.add(`${effectiveTheme}-theme`);

    // Also set data attribute for CSS targeting
    root.setAttribute("data-theme", effectiveTheme);
  }, [theme, systemPreference]);

  const setAppTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("app-theme", newTheme);
  };

  const getEffectiveTheme = () => {
    return theme === "auto" ? systemPreference : theme;
  };

  const value = {
    theme,
    setTheme: setAppTheme,
    effectiveTheme: getEffectiveTheme(),
    systemPreference,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
